import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    PiGridFourBold,
    PiHardDrivesBold,
    PiListBold,
    PiMagnifyingGlassBold,
    PiFilesBold,
    PiCheckCircleBold,
    PiClockBold,
} from 'react-icons/pi';
import { Input, LoadingState, PageHeader, Text } from '@v2/design-system';
import TablePagination, { TABLE_PER_PAGE_OPTIONS } from '@v2/components/TablePagination';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import FileGridCard from '@v2/features/file-manager/FileGridCard';
import FileListRow from '@v2/features/file-manager/FileListRow';
import FileManagerSidebar from '@v2/features/file-manager/FileManagerSidebar';
import FilePreviewModal from '@v2/features/file-manager/FilePreviewModal';
import type { SidebarFilter } from '@v2/features/file-manager/categories';
import type { FileManagerResponse, PloomesFileItem, SortMode, ViewMode } from '@v2/features/file-manager/types';

function filterToParams(filter: SidebarFilter): Record<string, string> {
    switch (filter.type) {
        case 'all':
            return {};
        case 'receipts':
            return { is_receipt: '1' };
        case 'pending':
            return { is_validated: '0' };
        case 'validated':
            return { is_validated: '1' };
        case 'category':
            return { category: filter.category };
    }
}

function sortToParam(sort: SortMode): string {
    switch (sort) {
        case 'name':
            return 'name';
        case 'size':
            return '-size';
        default:
            return 'recent';
    }
}

export default function PloomesAttachmentListPage() {
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<SidebarFilter>({ type: 'all' });
    const [view, setView] = useState<ViewMode>('grid');
    const [sort, setSort] = useState<SortMode>('recent');
    const [selected, setSelected] = useState<PloomesFileItem | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);

    useEffect(() => {
        setPage(1);
    }, [search, filter, sort, view]);

    const params = useMemo(() => {
        const p: Record<string, string | number> = {
            page,
            per_page: perPage,
            ...filterToParams(filter),
        };
        if (search.trim()) p.search = search.trim();
        const sortParam = sortToParam(sort);
        if (sortParam !== 'recent') p.sort = sortParam;
        return p;
    }, [search, filter, sort, page, perPage]);

    const { data, isLoading } = useQuery({
        queryKey: ['ploomes-attachments', params],
        queryFn: async () => (await api.get<FileManagerResponse>('/ploomes-attachments', { params })).data,
    });

    const files = data?.data ?? [];
    const stats = data?.meta?.stats;
    const listMeta = data?.meta;

    return (
        <div className="space-y-6">
            <PageHeader
                title="Gerenciador de arquivos"
                breadcrumb={[{ name: 'Dashboard', href: '/' }, { name: 'Ploomes' }, { name: 'Documentos' }]}
            >
                <Text className="text-sm text-gray-500">Documentos importados do Ploomes CRM</Text>
            </PageHeader>

            {stats && (
                <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <StatCard icon={PiFilesBold} label="Total de arquivos" value={String(stats.total)} tone="sky" />
                    <StatCard icon={PiHardDrivesBold} label="Armazenamento" value={stats.readable_total_size} tone="violet" />
                    <StatCard icon={PiCheckCircleBold} label="Validados" value={String(stats.validated)} tone="emerald" />
                    <StatCard icon={PiClockBold} label="Pendentes" value={String(stats.pending)} tone="amber" />
                </div>
            )}

            <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <div className="flex min-h-[32rem] flex-col lg:flex-row">
                    <div className="w-full shrink-0 lg:w-64 xl:w-72">
                        <FileManagerSidebar active={filter} onChange={setFilter} stats={stats} />
                    </div>

                    <div className="min-w-0 flex-1">
                        {/* Barra de ferramentas */}
                        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700/60">
                            <div className="relative min-w-[12rem] flex-1">
                                <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                                <Input
                                    type="search"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Buscar documentos…"
                                    className="ps-9"
                                />
                            </div>

                            <select
                                value={sort}
                                onChange={(e) => setSort(e.target.value as SortMode)}
                                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                            >
                                <option value="recent">Mais recentes</option>
                                <option value="name">Nome A–Z</option>
                                <option value="size">Maior tamanho</option>
                            </select>

                            <div className="flex rounded-lg border border-gray-200 p-0.5 dark:border-gray-700">
                                <ViewToggle active={view === 'grid'} onClick={() => setView('grid')} icon={PiGridFourBold} label="Grade" />
                                <ViewToggle active={view === 'list'} onClick={() => setView('list')} icon={PiListBold} label="Lista" />
                            </div>
                        </div>

                        {/* Contador */}
                        <div className="border-b border-gray-100 px-4 py-2 dark:border-gray-700/50">
                            <Text className="text-xs text-gray-500 dark:text-gray-400">
                                {isLoading ? 'Carregando…' : `${listMeta?.total ?? files.length} arquivo(s) encontrado(s)`}
                            </Text>
                        </div>

                        {isLoading ? (
                            <div className="p-8">
                                <LoadingState label="Carregando documentos…" />
                            </div>
                        ) : files.length === 0 ? (
                            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
                                <PiFilesBold className="size-12 text-gray-300 dark:text-gray-600" />
                                <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">Nenhum documento encontrado</p>
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">Tente outro filtro ou termo de busca.</p>
                            </div>
                        ) : view === 'grid' ? (
                            <div className="grid grid-cols-2 gap-4 p-4 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                                {files.map((file) => (
                                    <FileGridCard key={file.id} file={file} onClick={() => setSelected(file)} />
                                ))}
                            </div>
                        ) : (
                            <div>
                                {files.map((file) => (
                                    <FileListRow key={file.id} file={file} onClick={() => setSelected(file)} />
                                ))}
                            </div>
                        )}
                        {listMeta && (
                            <TablePagination
                                meta={{
                                    current_page: listMeta.current_page,
                                    last_page: listMeta.last_page,
                                    per_page: listMeta.per_page ?? perPage,
                                    total: listMeta.total,
                                    from: listMeta.from,
                                    to: listMeta.to,
                                }}
                                onPageChange={setPage}
                                onPerPageChange={(value) => {
                                    setPerPage(value);
                                    setPage(1);
                                }}
                                perPageOptions={[...TABLE_PER_PAGE_OPTIONS, 48]}
                                itemLabel="arquivo"
                            />
                        )}
                    </div>
                </div>
            </div>

            <FilePreviewModal file={selected} onClose={() => setSelected(null)} />
        </div>
    );
}

function StatCard({
    icon: Icon,
    label,
    value,
    tone,
}: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: string;
    tone: 'sky' | 'violet' | 'emerald' | 'amber';
}) {
    const tones = {
        sky:     'bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400',
        violet:  'bg-violet-50 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400',
        emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
        amber:   'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className={cn('mb-3 inline-flex size-10 items-center justify-center rounded-xl', tones[tone])}>
                <Icon className="size-5" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        </div>
    );
}

function ViewToggle({
    active,
    onClick,
    icon: Icon,
    label,
}: {
    active: boolean;
    onClick: () => void;
    icon: React.ComponentType<{ className?: string }>;
    label: string;
}) {
    return (
        <button
            type="button"
            title={label}
            onClick={onClick}
            className={cn(
                'rounded-md p-2 transition',
                active
                    ? 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200',
            )}
        >
            <Icon className="size-4" />
        </button>
    );
}

